/**
 * Pure planner for slot deletions. Mirrors the symmetry with slotInsert: when
 * a channel slot is deleted, every later occupied slot shifts down by one so
 * the channel stays compact (no leading gaps within a contiguous block).
 *
 * For AI-reference targets the four slots are role-based (front/back/etc.),
 * so deletion only clears that one slot — no compacting.
 */
import type { AddToProductTarget } from '../types'

export interface DeleteShift {
  channelId: string
  from: number
  to: number
  linkId?: string
}

export interface AppliedDelete {
  target: AddToProductTarget
  shifts: DeleteShift[]
}

export interface ChannelSlotEntry {
  linkId: string
}

export type ChannelSlotMap = Record<number, ChannelSlotEntry>

export function applyDelete(
  target: AddToProductTarget,
  currentSlotsForChannel: ChannelSlotMap = {},
): AppliedDelete {
  if (target.type !== 'channel') {
    return { target, shifts: [] }
  }
  const laterSlots = Object.keys(currentSlotsForChannel)
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > target.slot)
    .sort((a, b) => a - b)
  const shifts: DeleteShift[] = laterSlots.map((slot) => ({
    channelId: target.channelId,
    from: slot,
    to: slot - 1,
    linkId: currentSlotsForChannel[slot]?.linkId,
  }))
  return { target, shifts }
}
